import { BASE_PATHS, PATHS } from '@belong/common';
import {
  ListingApplicationApplicantModel,
  ListingApplicationApplicationModel,
  ListingApplicationListingApplicationModel,
  VerificationModel,
} from 'api/models';
import { SignerCurrentData } from './SignatureStatusUtils';

type CTA = {
  label: string;
  // Required: both of the CTAs built below define one, and the card that renders
  // them calls it unconditionally.
  onClick: (param: any) => void;
};

export interface ScreenResult {
  screen: ApplicationScreen;
  screenNumber: number;
  reason: string;
  stepNumber: 1 | 2 | 3 | 4 | 5 | 6 | 0;
  showLeaseAdvisorContact?: boolean;
  cardTitle?: string;
  cardDescription?: string | React.ReactNode;
  showWarningIcon?: boolean;
  cta1?: CTA;
  cta2?: CTA;
  showApplicantsStatus?: boolean;
  showVerificationsStatus?: boolean;
  showIncomeButton?: boolean;
  hideSteps?: boolean;
  signers?: SignerCurrentData[];
  signingUrl?: string;
  isSelfServe?: boolean;
}

export enum ApplicationScreen {
  StartedNotCompleted = 'StartedNotCompleted',
  GroupAppIncompleteIndividuals = 'GroupAppIncompleteIndividuals',
  CompletedNoVerifications = 'CompletedNoVerifications',
  IdVerifications = 'IdVerifications',
  IncomeAutoFailed = 'IncomeAutoFailed',
  IncomeAutoMismatch = 'IncomeAutoMismatch',
  CompletedCosignerRequiredNoCoSigner = 'CompletedCosignerRequiredNoCoSigner',
  CosignerAddedNotCompleted = 'CosignerAddedNotCompleted',
  CosignerSubmittedPendingVerifications = 'CosignerSubmittedPendingVerifications',
  CosignerAddedDoesNotMeetCriteria = 'CosignerAddedDoesNotMeetCriteria',
  SoftDisqualifyingFactor = 'SoftDisqualifyingFactor',
  LeaseRejected = 'LeaseRejected',
  LeaseNotOffered = 'LeaseNotOffered',
  QualifiedOrWaitlistedDesign = 'QualifiedOrWaitlistedDesign',
  WithdrawnFallback = 'WithdrawnFallback',
  Default = 'Default',
}

// --- helpers ------------------------------------------------------------

const hasApplicantType = (
  app: ListingApplicationApplicationModel | undefined,
  type: 'MainApplicant' | 'CoApplicant' | 'CoSigner',
) => !!app?.applicants?.some((a) => a.applicantType === type);

function findGuarantorApplicant(app?: ListingApplicationApplicationModel) {
  if (!app?.applicants) return undefined;
  return app.applicants.find((a) => a.applicantType === 'CoSigner');
}

// const anyApplicantHasStatus = (
//   app: ListingApplicationApplicationModel | undefined,
//   statuses: ListingApplicationApplicantModel['status'][]
// ) => !!app?.applicants?.some((a) => statuses.includes(a.status));

// Generic so the return type is exactly the argument's: this mutates and hands
// back the same object, and the caller has already ruled out `undefined` before
// reassigning `currentApplicant` from it. Declaring the parameter as
// `Model | undefined` instead widened the variable back and made every later
// read possibly-undefined.
function removeDisabledIncomeVerificationsForApplicant<
  T extends ListingApplicationApplicantModel | undefined,
>(applicant: T): T {
  if (!applicant?.verifications) return applicant;
  applicant.verifications = applicant.verifications.filter(
    (v) => !(v.verificationType === 'Income' && v.status === 'Disabled'),
  );
  return applicant;
}

const currentApplicantHasVerification = (
  applicant: ListingApplicationApplicantModel | undefined,
  predicate: (v: VerificationModel) => boolean,
) => !!applicant?.verifications?.some(predicate);

const findIdentityVerification = (
  applicant?: ListingApplicationApplicantModel,
) => applicant?.verifications?.find((v) => v.verificationType === 'Identity');

// --- main resolver ------------------------------------------------------

export function resolveApplicationScreen(
  listing?: ListingApplicationListingApplicationModel,
  currentApplicant?: ListingApplicationApplicantModel,
  applicationId?: string,
  unitId?: string,
): ScreenResult {
  if (!listing || !currentApplicant || !listing.application) {
    return {
      screen: ApplicationScreen.Default,
      screenNumber: 1,
      reason: 'no listing',
      stepNumber: 1,
    };
  }

  currentApplicant =
    removeDisabledIncomeVerificationsForApplicant(currentApplicant);

  const app = listing.application as ListingApplicationApplicationModel;

  const guarantor = findGuarantorApplicant(app);

  // 1. Started application but not completed
  if (
    currentApplicant &&
    (currentApplicant.status === 'Invited' ||
      currentApplicant.status === 'Started')
  ) {
    return {
      screen: ApplicationScreen.StartedNotCompleted,
      screenNumber: 1,
      reason: 'currentApplicant Invited|Started',
      stepNumber: 1,
    };
  }

  // 2. Group application with incomplete individual applicants
  if (
    app?.status === 'Started' &&
    (currentApplicant.status === 'Submitted' ||
      currentApplicant.status === 'VerificationsCompleted')
  ) {
    return {
      screen: ApplicationScreen.GroupAppIncompleteIndividuals,
      screenNumber: 2,
      reason: 'app.Started & some applicants Submitted|VerificationsCompleted',
      stepNumber: 1,
      showApplicantsStatus: true,
      cardTitle: "We're waiting for your co-applicants to\u00A0complete.",
      cardDescription:
        'We’ll begin reviewing your application once everyone has submitted their information.',
      showWarningIcon: true,
    };
  }

  // 4. ID Verifications Screen
  if (
    app?.status === 'Submitted' &&
    // Asserted, not optional-chained: an applicant with no identity
    // verification threw here before and taking the other branch instead would
    // change which screen is shown.
    findIdentityVerification(currentApplicant)!.status !== 'Passed'
  ) {
    const identity = findIdentityVerification(currentApplicant);
    const ok =
      identity &&
      (identity.status === 'Passed' ||
        identity.status === 'RequiresManualReview');
    if (!ok) {
      return {
        screen: ApplicationScreen.IdVerifications,
        screenNumber: 4,
        reason: 'app.Submitted & identity not Passed|RequiresManualReview',
        stepNumber: 2,
        cardTitle: 'Your ID verification\u00A0failed.',
        showWarningIcon: true,
        showLeaseAdvisorContact: true,
        cardDescription:
          'We couldn’t verify your\u00A0ID. Try again using a clear, well-lit photo of your ID on a plain\u00A0background. Make sure all details are visible, and follow the head movement steps\u00A0slowly.',
      };
    }
  }

  // 5. Income verification failed from Plaid
  if (listing.status === 'None') {
    const hasIncomeFailed = currentApplicantHasVerification(
      currentApplicant,
      (v) =>
        v.verificationType === 'Income' &&
        (v.status === 'Failed' || v.status === 'Rejected'),
    );
    if (hasIncomeFailed) {
      return {
        screen: ApplicationScreen.IncomeAutoFailed,
        screenNumber: 5,
        reason: 'listing.None & income Failed/Rejected',
        stepNumber: 2,
        showLeaseAdvisorContact: true,
        showWarningIcon: true,
        cardTitle: 'We couldn’t verify your\u00A0income.',
        cardDescription: (
          <>
            For income verification, provide your Leasing Advisor with either
            your last <strong>four paystubs</strong> (PDF only) or your last{' '}
            <strong>four personal bank statements</strong> (PDF only).
          </>
        ),
      };
    }
  }

  // 6. Income verification mismatch
  if (listing.status === 'None') {
    const hasMismatch = currentApplicantHasVerification(
      currentApplicant,
      (v) => v.verificationType === 'Income' && !!v.incomeVerificationMismatch,
    );
    if (hasMismatch) {
      return {
        screen: ApplicationScreen.IncomeAutoMismatch,
        screenNumber: 6,
        reason: 'listing.None & incomeVerificationMismatch == true',
        stepNumber: 2,
        showLeaseAdvisorContact: true,
        showWarningIcon: true,
        cardTitle: 'Your income details need a quick\u00A0check.',
        cardDescription:
          'There’s a mismatch in the income information we\u00A0reviewed. Your Leasing Advisor can help you to provide additional income\u00A0sources.',
      };
    }
  }

  // 7. Completed Application with Status as Guarantor Required (no CoSigner)
  if (
    listing.status === 'CoSignerRequired' &&
    !hasApplicantType(app, 'CoSigner')
  ) {
    return {
      screen: ApplicationScreen.CompletedCosignerRequiredNoCoSigner,
      screenNumber: 7,
      reason: 'CoSignerRequired & no CoSigner',
      stepNumber: 2,
      cardTitle: 'Additional information may improve your\u00A0application.',
      showWarningIcon: true,
      cta1: {
        label: 'Add Guarantor',
        onClick: (navigate: any) => {
          navigate(
            PATHS.RESIDENT_APPLICATION_CO_SIGNER_STEP.concat(
              `?applicationId=${applicationId}&homeId=${unitId}`,
            ),
          );
        },
      },
      showIncomeButton: true,
      cardDescription: (
        <>
          Based on what we’ve reviewed so far, your application may benefit from{' '}
          <strong>adding a&nbsp;Guarantor</strong>, or contact your <br />
          Leasing Advisor if <strong>you have more income to add</strong> to
          your&nbsp;application.
        </>
      ),
    };
  }

  // 8. Guarantor Added but not completed the application
  if (
    listing.status === 'CoSignerRequired' &&
    hasApplicantType(app, 'CoSigner')
  ) {
    if (
      guarantor &&
      (guarantor.status === 'Invited' || guarantor.status === 'Started')
    ) {
      return {
        screen: ApplicationScreen.CosignerAddedNotCompleted,
        screenNumber: 8,
        reason: 'CoSignerRequired & CoSigner present & app.Started',
        stepNumber: 2,
        cardTitle: 'Waiting for your Guarantor.',
        cardDescription:
          'You’ve added a Guarantor to support your application, but they haven’t completed their part\u00A0yet. We’ll begin reviewing again once all required Guarantor information is\u00A0submitted.',
      };
    }
  }

  // 9. Guarantor Submitted but Verifications are not Completed
  if (
    listing.status === 'CoSignerRequired' &&
    hasApplicantType(app, 'CoSigner')
  ) {
    if (guarantor && guarantor.status === 'Submitted') {
      return {
        screen: ApplicationScreen.CosignerSubmittedPendingVerifications,
        screenNumber: 9,
        reason: 'CoSignerRequired & CoSigner present & app.Submitted',
        stepNumber: 2,
        cardTitle: 'We’re processing the updated\u00A0application.',
        cardDescription: (
          <>
            Thanks for adding a&nbsp;Guarantor. We’re now reviewing their
            information, along with your group’s full&nbsp;application.{' '}
            <strong>This takes 1–2 business&nbsp;days</strong>. You’ll hear back
            from us as soon as we’re&nbsp;done.
          </>
        ),
      };
    }
  }

  // 10. Guarantor Added but doesn't meet criteria
  if (
    listing.status === 'CoSignerRequired' &&
    hasApplicantType(app, 'CoSigner')
  ) {
    if (guarantor && guarantor.status === 'VerificationsCompleted') {
      return {
        screen: ApplicationScreen.CosignerAddedDoesNotMeetCriteria,
        screenNumber: 10,
        reason:
          'CoSignerRequired & CoSigner present & app.VerificationsCompleted',
        stepNumber: 2,
        cardTitle: 'Guarantor doesn’t meet\u00A0requirements.',
        cardDescription:
          'Your Guarantor completed their part, but they don’t meet the\u00A0requirements. Please contact your Leasing Advisor to discuss income\u00A0options.',
        showWarningIcon: true,
        showLeaseAdvisorContact: true,
      };
    }
  }

  // 3.5. Completed application without verifications complete
  if (
    app?.status === 'Submitted' &&
    currentApplicant.status === 'VerificationsCompleted'
  ) {
    return {
      screen: ApplicationScreen.CompletedNoVerifications,
      screenNumber: 3.5,
      reason: 'app.Submitted',
      stepNumber: 2,
      cardTitle: 'We’re processing the\u00A0application.',
      showVerificationsStatus: true,
      cardDescription:
        'Your application is complete! We’re processing it now and will get back to you within 1–2 business days.',
    };
  }

  // 3. Completed application without verifications complete
  if (app?.status === 'Submitted') {
    return {
      screen: ApplicationScreen.CompletedNoVerifications,
      screenNumber: 3,
      reason: 'app.Submitted',
      stepNumber: 2,
      cardTitle: 'Thanks for completing your\u00A0application!',
      showVerificationsStatus: true,
      cardDescription:
        "We're now reviewing your information, along with your group's full application. This takes 1-2 business\u00A0days.  You'll hear back from us as soon as we're\u00A0done.",
    };
  }

  // 11. Soft disqualifying factor
  if (listing.status === 'NotQualified') {
    return {
      screen: ApplicationScreen.SoftDisqualifyingFactor,
      screenNumber: 11,
      reason: 'listing.NotQualified',
      stepNumber: 2,
      cardTitle: 'Your application Is under Advisor\u00A0review.',
      cardDescription:
        'We noticed something in your application that needs additional\u00A0review. Your Leasing Advisor will be in touch soon to go over the details and next steps. No action is required from you at this\u00A0time.',
      showWarningIcon: true,
    };
  }

  // 12. Lease Rejected
  if (listing.status === 'Rejected') {
    return {
      screen: ApplicationScreen.LeaseRejected,
      screenNumber: 12,
      reason: 'listing.Rejected',
      stepNumber: 0,
      hideSteps: true,
      cardTitle: 'Unfortunately, this application was not\u00A0approved.',
      cardDescription:
        'Your application was\u00A0reviewed, but it didn’t meet our qualification\u00A0requirements.',
      // TO DO: add a learn more button that takes to requirmients landing page
    };
  }

  // 13. Lease Not Offered
  if (listing.status === 'NotOffered') {
    return {
      screen: ApplicationScreen.LeaseNotOffered,
      screenNumber: 13,
      reason: 'listing.NotOffered',
      stepNumber: 0,
      hideSteps: true,
      cardTitle: 'The home was offered to another\u00A0applicant.',
      cardDescription:
        'Thanks for applying, but the home was offered to someone\u00A0else.',
      cta1: {
        label: 'See Similar Homes',
        onClick: (navigate: any) => {
          navigate(BASE_PATHS.HOME);
        },
      },
    };
  }

  // 14. Qualified or Waitlisted
  if (listing.status === 'Qualified' || listing.status === 'Waitlisted') {
    return {
      screen: ApplicationScreen.QualifiedOrWaitlistedDesign,
      screenNumber: 14,
      reason: 'listing.Qualified|Waitlisted',
      stepNumber: 3,
      cardTitle: 'We’re processing the\u00A0application.',
      cardDescription:
        'Your application is\u00A0complete! We’re processing it now and will get back to you within 1–2 business\u00A0days.',
      showVerificationsStatus: true,
    };
  }

  // This cases should never happen but we have them here for safety

  // 15. Withdrawn
  if (listing.status === 'Withdrawn') {
    return {
      screen: ApplicationScreen.WithdrawnFallback,
      screenNumber: 15,
      reason: 'listing.Withdrawn',
      stepNumber: 0,
      hideSteps: true,
      cardTitle: 'Your application was\u00A0withdrawn.',
      cardDescription:
        'The application has been closed as\u00A0withdrawn. If you’d like to apply\u00A0again, you can start a new application\u00A0anytime.',
    };
  }

  // Default
  return {
    screen: ApplicationScreen.CompletedNoVerifications,
    screenNumber: 3.5,
    reason: 'app.Submitted',
    stepNumber: 2,
    cardTitle: 'We’re processing the\u00A0application.',
    cardDescription:
      'Your application is complete! We’re processing it now and will get back to you within 1–2 business days.',
  };
}
